import { ApplicationRef, Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { ContentService } from '@blk/cwp-ng-framework';
import { AppConfigService } from '@ddt-web/app-configuration';
import { APP_BOOTSTRAP_SERVICE_FLAGS, AppBootstrapServiceFlags, IS_PUBLIC_APPLICATION } from '@ddt-web/config';
import { ConfigService } from '@ddt-web/config-layer';
import { FeatureFlagLoaderService } from '@ddt-web/feature-flag';
import { UrlUtils } from '@ddt-web/url';
import { InstanceVariantsService } from '@ddt-web/user-data';

import { AuthUtil, OktaAuthenticationService, UserPlatformAuthenticationService } from '../auth';

@Injectable({
	providedIn: 'root'
})
export class AppBootstrapService {

	constructor(
		private oktaAuthenticationService: OktaAuthenticationService,
		private userPlatformAuthenticationService: UserPlatformAuthenticationService,
		@Inject('bootstrapComponents') private bootstrapComponents: { string: any },
		private appConfigService: AppConfigService,
		private featureFlagLoaderService: FeatureFlagLoaderService,
		private configService: ConfigService,
		private variantsService: InstanceVariantsService,
		@Inject('environment') private environment: any,
		@Optional() private router: Router,
		@Optional() @Inject(APP_BOOTSTRAP_SERVICE_FLAGS) private appBootstrapServiceFlags: AppBootstrapServiceFlags,
		@Optional() @Inject(IS_PUBLIC_APPLICATION) private isPublicApp: boolean
	) { }

	public bootstrap(appRef: ApplicationRef, authSuccessCallback?: Function): void {

		// for partner authentication, the window gets closed for localhost if the closeWindow param is present
		if (window.location.href.includes('closeWindow=true')) {
			window.close();
		}
		if (AuthUtil.isPublicAppLogin()) {
			window['BLK'].isCwpUserSignedIn = true;
		}
		const isCwpUserSignedIn = window?.['BLK']?.isCwpUserSignedIn ?? false;
		if ((!this.isPublicApp || isCwpUserSignedIn) && !UrlUtils.isInternalHost()) {
			this.checkAuthentication(authSuccessCallback);
		}

		this.bootstrapApp(appRef, isCwpUserSignedIn);
	}

	/**
	 * TODO dbuddha: This is temporary as a stop gap. To be removed later
	 * This method redirects /?_path=%2Foverview&portfolios=403c62b7-cf20-4629-83fe-fde77e020524
	 * to /overview?portfolios=403c62b7-cf20-4629-83fe-fde77e020524
	 */
	public redirectLegacyUrl(): void {
		const queryParams = new URLSearchParams(window.location.search);
		let pathParam = queryParams.get('_path');
		if (pathParam && this.router) {
			const enhancedEncodeURIComponent = (str) => encodeURIComponent(str).replace(/[!'()*]/g, character => '%' + character.charCodeAt(0).toString(16));

			const updatedQueryParams = window.location.search
				.replace('_path=' + enhancedEncodeURIComponent(pathParam), '')
				.replace('_path=' + pathParam, '') // this is for env deep link case because pathParam is /overview and not %2Foverview
				.replace('?&', '');

			pathParam = pathParam.replace(new RegExp(/(view\/)?\(editor:(\w+)\)/), '$2'); // for pe
			const url = pathParam + '?' + updatedQueryParams;

			console.log('Redirecting legacy URL', url);
			this.router.navigateByUrl(url);
		}
	}

	private checkAuthentication(authSuccessCallback?: Function): void {
		this.redirectLegacyUrl();

		const shouldUseOkta = AuthUtil.isOktaEnabled(this.environment.cloud);
		if (shouldUseOkta) {
			this.oktaAuthenticationService.checkAuthentication(authSuccessCallback);
		} else {
			this.userPlatformAuthenticationService.checkAuthentication(authSuccessCallback);
		}
	}

	private async fetchConfigVariants(): Promise<void> {
		if (this.appBootstrapServiceFlags?.isConfigServiceEnabled !== false) {
			const variants: string[] = await this.variantsService.getConfigVariants().toPromise();
			await this.configService.init([...variants]);
		}
	}

	// eslint-disable-next-line
	private async bootstrapApp(appRef: ApplicationRef, isCwpUserSignedIn: boolean) {
		// TODO chmalcol: Consider moving some of this out. Config service could go into a router guard for example.
		this.appConfigService.fetchAppConfigData();

		if (!this.isPublicApp || isCwpUserSignedIn) {
			await this.fetchConfigVariants();
			if (this.appBootstrapServiceFlags?.isFeatureFlagServiceEnabled !== false) {
				await this.featureFlagLoaderService.init();
			}
		}

		// Bootstrap components
		for (const componentTag in this.bootstrapComponents) {
			if (document.querySelectorAll(componentTag).length) {
				const component = this.bootstrapComponents[componentTag];
				appRef.bootstrap(component);
			}
		}
	}

	public bootstrapDcr(contentService: ContentService): void {
		const dcrCombined = Object.assign({}, this.environment?.dcr ?? {}, contentService['_dcrContent']);
		contentService.initialiseDCRContentFromObj(dcrCombined);
	}
}
